import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PaymentFormData, PaymentMode, TFN } from '../types/payment';
import { ALLOWED_CATEGORIES, US_STATES, US_STATE_CODES } from '../constants/enums';
import { detectCardBrand, mapBrandToCategory } from '../utils/cardUtils';
import { SettingsAPI, PaymentLinkAPI } from '../services/api';
import { MyProcessorRouting } from '../types/settings';
import { useAuth } from '../context/AuthContext';

// Helper to make optional fields that validate format only when non-empty
const optionalWithFormat = (schema: z.ZodString) =>
  z.string().optional().superRefine((val, ctx) => {
    if (!val || val === '') return; // Empty is OK
    const result = schema.safeParse(val);
    if (!result.success) {
      // Zod v4 uses .issues instead of .errors
      const issues = result.error.issues || (result.error as any).errors || [];
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issues[0]?.message || 'Invalid format',
      });
    }
  }).transform((val) => (val === '' ? undefined : val));

// Parse an MM/DD/YYYY string to a midnight Date, or null if not a full valid date.
const parseMDY = (v?: string): Date | null => {
  const m = (v || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return isNaN(d.getTime()) ? null : d;
};
const startOfToday = (): Date => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
// DLN issue date can't be in the future; expiry can't be in the past. Only
// enforced once a full valid date is entered (empty/partial is left to format rules).
const issueNotFuture = (v?: string) => { const d = parseMDY(v); return !d || d <= startOfToday(); };
const expiryNotPast = (v?: string) => { const d = parseMDY(v); return !d || d >= startOfToday(); };

const paymentSchema = z.object({
  orderid: z.string().optional(),
  ponumber: z.string().optional(),
  order_description: z.string().optional(),
  shipping_amount: z.coerce.number().optional(),
  sales_tax: z.coerce.number().optional(),
  tax_exempt: z.boolean().optional(),
  same_as_billing: z.boolean().optional(),
  shipping_firstname: z.string().optional(),
  shipping_lastname: z.string().optional(),
  shipping_company: z.string().optional(),
  shipping_address_1: z.string().optional(),
  shipping_address_2: z.string().optional(),
  shipping_city: z.string().optional(),
  shipping_state: z.string().optional(),
  shipping_zip: z.string().optional(),
  shipping_country: z.string().optional(),
  // Date of birth: optional but must be valid MM/DD/YYYY format if provided
  date_of_birth: optionalWithFormat(
    z.string().regex(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/,
      'Date of birth must be in MM/DD/YYYY format (e.g., 09/10/1990)'
    )
  ).refine(issueNotFuture, 'Date of birth cannot be in the future'),
  account_number: z.string().optional(),
  ssn: z.string().regex(/^\d{9}$/, 'SSN must be exactly 9 digits'),
  drivers_license_number: z.string().optional(),
  drivers_license_state: z.string().optional(),
  dl_issuing_date: z.string().optional().refine(issueNotFuture, 'Issue date cannot be in the future'),
  dl_expiration_date: z.string().optional().refine(expiryNotPast, 'Expiration date cannot be in the past'),
  ccnumber: z.string().min(1, 'Card number is required').superRefine((val, ctx) => {
    const digits = val.replace(/\D/g, '');
    const cardBrand = detectCardBrand(digits);
    // Amex cards are 15 digits, all others are 16
    if (cardBrand === 'amex') {
      if (digits.length !== 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'American Express cards must be exactly 15 digits',
        });
      }
    } else if (digits.length !== 16) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Card number must be exactly 16 digits',
      });
    }
  }),
  ccexp: z.string().min(4, 'Expiry date is required').max(4, 'Expiry must be 4 digits (MMYY)'),
  cvv: z.string().min(1, 'CVV is required'),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  company: z.string().optional(),
  address: z.string().min(1, 'Address is required'),
  address_cont: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'ZIP code is required').refine(
    (val) => /^\d{5}$/.test(val),
    'ZIP code must be exactly 5 digits'
  ),
  country: z.string().optional(),
  // Phone number: optional but must be exactly 10 numeric digits if provided
  phone_number: optionalWithFormat(
    z.string().refine(
      (val) => /^\d{10}$/.test(val),
      'Phone number must be exactly 10 numeric digits (no dashes or spaces)'
    )
  ),
  // Email: optional but must be valid email format if provided
  email_address: optionalWithFormat(
    z.string().email('Email must be a valid email address (e.g., user@example.com)')
  ),
  website_address: z.string().optional(),
  // Fax number: optional but must be valid format if provided
  fax_number: optionalWithFormat(
    z.string().regex(
      /^[\d\s\-()+ ]{7,20}$/,
      'Fax number must be a valid format (e.g., 123-456-7890)'
    )
  ),
  test_mode: z.boolean().optional(),
  sale_type: z.string().refine((val) => ALLOWED_CATEGORIES.includes(val as typeof ALLOWED_CATEGORIES[number]), {
    message: 'Sale type is required'
  }),
  sale_state: z.string().refine((val) => US_STATES.includes(val as typeof US_STATES[number]), {
    message: 'Sale state is required'
  }),
  processor_id: z.string().optional(),
  nmi_account: z.string().optional(),
  // New fields (mandatory)
  sale_domains: z.array(z.string()).min(1, 'At least one sale domain is required'),
  utility_providers: z.record(z.string(), z.string()).refine((providers) => {
    return Object.keys(providers).length > 0;
  }, 'At least one utility provider must be selected'),
  sale_notes: z.string().min(1, 'Sale notes are required'),
  tfn: z.string().min(1, 'TFN is required'),
}).superRefine((data, ctx) => {
  // Cross-field validation for CVV based on card type
  const cardDigits = data.ccnumber?.replace(/\D/g, '') || '';
  const cvvDigits = data.cvv?.replace(/\D/g, '') || '';
  const cardBrand = detectCardBrand(cardDigits);

  // Amex requires 4-digit CVV, all other cards require 3-digit CVV
  if (cardBrand === 'amex') {
    if (cvvDigits.length !== 4) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'American Express cards require a 4-digit CVV',
        path: ['cvv'],
      });
    }
  } else if (cardDigits.length >= 4) {
    // Only validate CVV length if we have enough card digits to detect brand
    if (cvvDigits.length !== 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CVV must be exactly 3 digits',
        path: ['cvv'],
      });
    }
  }

  // Cross-field validation: each selected domain must have a provider selected
  const selectedDomains = data.sale_domains || [];
  const selectedProviders = data.utility_providers || {};

  for (const domain of selectedDomains) {
    if (!selectedProviders[domain] || selectedProviders[domain] === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Please select a provider for ${domain}`,
        path: ['utility_providers'],
      });
    }
  }

  // Service Address is required — unless "same as billing" (then it's copied
  // from billing, so no separate entry is needed).
  if (!data.same_as_billing) {
    const blank = (v?: string) => !v || v.trim() === '';
    if (blank(data.shipping_address_1)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service address is required', path: ['shipping_address_1'] });
    if (blank(data.shipping_city)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service city is required', path: ['shipping_city'] });
    if (blank(data.shipping_state)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service state is required', path: ['shipping_state'] });
    if (blank(data.shipping_zip)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service ZIP code is required', path: ['shipping_zip'] });
    else if (data.shipping_zip && !/^\d{5}$/.test(data.shipping_zip)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service ZIP code must be exactly 5 digits', path: ['shipping_zip'] });
  }
});

// Payment link schema — same as paymentSchema but without card fields
const paymentLinkSchema = z.object({
  orderid: z.string().optional(),
  ponumber: z.string().optional(),
  order_description: z.string().optional(),
  shipping_amount: z.coerce.number().optional(),
  sales_tax: z.coerce.number().optional(),
  tax_exempt: z.boolean().optional(),
  same_as_billing: z.boolean().optional(),
  shipping_firstname: z.string().optional(),
  shipping_lastname: z.string().optional(),
  shipping_company: z.string().optional(),
  shipping_address_1: z.string().optional(),
  shipping_address_2: z.string().optional(),
  shipping_city: z.string().optional(),
  shipping_state: z.string().optional(),
  shipping_zip: z.string().optional(),
  shipping_country: z.string().optional(),
  date_of_birth: optionalWithFormat(
    z.string().regex(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/,
      'Date of birth must be in MM/DD/YYYY format (e.g., 09/10/1990)'
    )
  ).refine(issueNotFuture, 'Date of birth cannot be in the future'),
  account_number: z.string().optional(),
  ssn: z.string().regex(/^\d{9}$/, 'SSN must be exactly 9 digits'),
  drivers_license_number: z.string().optional(),
  drivers_license_state: z.string().optional(),
  dl_issuing_date: z.string().optional().refine(issueNotFuture, 'Issue date cannot be in the future'),
  dl_expiration_date: z.string().optional().refine(expiryNotPast, 'Expiration date cannot be in the past'),
  // No card fields (ccnumber, ccexp, cvv)
  ccnumber: z.string().optional(),
  ccexp: z.string().optional(),
  cvv: z.string().optional(),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  company: z.string().optional(),
  address: z.string().min(1, 'Address is required'),
  address_cont: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'ZIP code is required').refine(
    (val) => /^\d{5}$/.test(val),
    'ZIP code must be exactly 5 digits'
  ),
  country: z.string().optional(),
  phone_number: optionalWithFormat(
    z.string().refine(
      (val) => /^\d{10}$/.test(val),
      'Phone number must be exactly 10 numeric digits (no dashes or spaces)'
    )
  ),
  email_address: optionalWithFormat(
    z.string().email('Email must be a valid email address (e.g., user@example.com)')
  ),
  website_address: z.string().optional(),
  fax_number: optionalWithFormat(
    z.string().regex(
      /^[\d\s\-()+ ]{7,20}$/,
      'Fax number must be a valid format (e.g., 123-456-7890)'
    )
  ),
  test_mode: z.boolean().optional(),
  sale_type: z.string().refine((val) => ALLOWED_CATEGORIES.includes(val as typeof ALLOWED_CATEGORIES[number]), {
    message: 'Sale type is required'
  }),
  sale_state: z.string().refine((val) => US_STATES.includes(val as typeof US_STATES[number]), {
    message: 'Sale state is required'
  }),
  processor_id: z.string().optional(),
  nmi_account: z.string().optional(),
  sale_domains: z.array(z.string()).min(1, 'At least one sale domain is required'),
  utility_providers: z.record(z.string(), z.string()).refine((providers) => {
    return Object.keys(providers).length > 0;
  }, 'At least one utility provider must be selected'),
  sale_notes: z.string().min(1, 'Sale notes are required'),
  tfn: z.string().min(1, 'TFN is required'),
}).superRefine((data, ctx) => {
  // Service Address is required — unless "same as billing" (then it's copied
  // from billing, so no separate entry is needed).
  if (!data.same_as_billing) {
    const blank = (v?: string) => !v || v.trim() === '';
    if (blank(data.shipping_address_1)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service address is required', path: ['shipping_address_1'] });
    if (blank(data.shipping_city)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service city is required', path: ['shipping_city'] });
    if (blank(data.shipping_state)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service state is required', path: ['shipping_state'] });
    if (blank(data.shipping_zip)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service ZIP code is required', path: ['shipping_zip'] });
    else if (data.shipping_zip && !/^\d{5}$/.test(data.shipping_zip)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Service ZIP code must be exactly 5 digits', path: ['shipping_zip'] });
  }
});

interface ProviderOption {
  id: string | number;
  name: string;
}

interface SearchableProviderSelectProps {
  options: ProviderOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
}

const SearchableProviderSelect: React.FC<SearchableProviderSelectProps> = ({
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Select a provider...',
  hasError,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find(o => String(o.id) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.name.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      // Focus the search input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const select = (id: string | number) => {
    onChange(String(id));
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) select(opt.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const displayText = selected ? selected.name : placeholder;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {!open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(true)}
          className={`form-select required-field ${hasError ? 'error-field' : ''}`}
          style={{
            width: '100%',
            textAlign: 'left',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
            background: 'var(--input-bg)',
            border: '1px solid var(--input-border)',
          }}
        >
          {displayText}
        </button>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={selected ? selected.name : 'Search providers...'}
          className={`form-select required-field ${hasError ? 'error-field' : ''}`}
          style={{
            width: '100%',
            background: 'var(--input-bg)',
            color: 'var(--text-primary)',
            border: '1px solid var(--input-border)',
          }}
          autoComplete="off"
        />
      )}
      {open && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            margin: '4px 0 0 0',
            padding: 0,
            listStyle: 'none',
            maxHeight: 240,
            overflowY: 'auto',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 6,
            boxShadow: '0 4px 12px var(--shadow-medium)',
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: '4px 8px', fontSize: '0.875rem', color: 'var(--text-muted)' }}>No matches</li>
          ) : (
            filtered.map((opt, idx) => {
              const isHighlighted = idx === highlight;
              const isSelected = String(opt.id) === String(value);
              return (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(opt.id);
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    background: isHighlighted ? 'var(--bg-hover)' : 'transparent',
                    fontWeight: isSelected ? 600 : 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {opt.name}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

interface PaymentFormProps {
  onSubmit: (data: PaymentFormData, type: 'sale' | 'authorize' | 'validate' | 'sale-3ri') => Promise<void>;
  loading: boolean;
  defaultValues?: Partial<PaymentFormData>;
}

type FormData = z.infer<typeof paymentSchema>;

export const PaymentForm: React.FC<PaymentFormProps> = ({ onSubmit, loading, defaultValues }) => {
  const { user } = useAuth();
  // Admins are the top of the hierarchy — their payment links bypass approval
  // and are sent immediately (email/SMS buttons). Everyone else submits the
  // link for a supervisor to approve & send.
  const isAdmin = user?.role === 'admin';
  // Self Authorization is restricted to floor managers and above.
  const canSelfAuth = ['floor_manager', 'director', 'admin'].includes(user?.role ?? '');
  // Roles that set the per-link 3DS toggle on the creation form. For an admin
  // it's final; for the others it's a proposed value their approver confirms.
  const canToggle3ds = ['admin', 'director', 'floor_manager', 'sales_team_lead'].includes(user?.role ?? '');

  const [processorSettings, setProcessorSettings] = useState<any>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [myRouting, setMyRouting] = useState<MyProcessorRouting | null>(null);
  const [routingLoaded, setRoutingLoaded] = useState(false);
  const [routingMissing, setRoutingMissing] = useState(false);

  // Payment mode: send payment link or self authorization.
  // Direct Payment is hidden from the UI, so default to the payment link flow.
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('payment_link');
  const [sendingLink, setSendingLink] = useState(false);
  // Active Toll-Free Numbers for the (mandatory) TFN dropdown.
  const [tfns, setTfns] = useState<TFN[]>([]);
  // QuickClick links go through the processor assigned to this user in Settings, so
  // they don't need the per-card routing (the card isn't known until NMI's page).
  const qcLink = paymentMode === 'payment_link' && !!processorSettings?.useQuickclick && !processorSettings?.testModeEnabled;
  // Per-link 3DS choice for the elevated roles (see canToggle3ds). Initialized
  // from the live global toggle once settings load. For admins it's the final,
  // authoritative value; for the other roles it's the proposed value their
  // approver sees as the default.
  const [linkThreeDs, setLinkThreeDs] = useState(true);

  // Provider and domain state
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [providersByDomain, setProvidersByDomain] = useState<Record<string, any[]>>({});
  const [loadingProviders, setLoadingProviders] = useState<Record<string, boolean>>({});

  // Dialog state for transaction result
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState<'success' | 'error'>('success');

  // Dialog state for 3DS-disabled confirmation (agent-facing only)
  const [threeDsConfirmOpen, setThreeDsConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [checkingThreeDs, setCheckingThreeDs] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(paymentSchema) as any,
    defaultValues: {
      currency: 'USD',
      country: 'US',
      shipping_country: 'US',
      test_mode: false,
      tax_exempt: false,
      same_as_billing: false,
      sale_type: ALLOWED_CATEGORIES[0] as typeof ALLOWED_CATEGORIES[number],
      sale_state: US_STATES[0] as typeof US_STATES[number],
      processor_id: '', // Default to empty (test mode) - will be set by settings
      nmi_account: '',
      // New fields
      sale_domains: [],
      utility_providers: {},
      sale_notes: '',
      tfn: '',
      ...defaultValues
    }
  });

  const sameAsBilling = watch('same_as_billing');
  const ccnumber = watch('ccnumber');
  // Drive the delivery-button visibility off the entered contact details:
  // only offer a channel that actually has a destination.
  const hasEmail = !!watch('email_address')?.trim();
  const hasPhone = !!watch('phone_number')?.trim();

  // Load processor settings on component mount
  useEffect(() => {
    const loadProcessorSettings = async () => {
      try {
        const settings = await SettingsAPI.getProcessorSettings();
        console.log('[PaymentForm] Loaded processor settings:', {
          testModeEnabled: settings.testModeEnabled
        });
        setProcessorSettings(settings);
        setLinkThreeDs(settings.threeDsEnabled !== false);
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Failed to load processor settings:', error);
        const defaultSettings = {
          testModeEnabled: false,
          updatedAt: new Date().toISOString(),
          updatedBy: 'system'
        };
        setProcessorSettings(defaultSettings);
        setSettingsLoaded(true);
      }
    };

    loadProcessorSettings();

    const loadMyRouting = async () => {
      try {
        const routing = await SettingsAPI.getMyRouting();
        console.log('[PaymentForm] Loaded my routing:', routing);
        setMyRouting(routing);
      } catch (error) {
        console.error('Failed to load my routing:', error);
        setMyRouting(null);
      } finally {
        setRoutingLoaded(true);
      }
    };

    loadMyRouting();

    const loadTfns = async () => {
      try {
        const res = await SettingsAPI.getTFNs(true); // active only, for the dropdown
        setTfns(res.tfns || []);
      } catch (error) {
        console.error('Failed to load TFNs:', error);
        setTfns([]);
      }
    };
    loadTfns();
  }, []);

  useEffect(() => {
    if (sameAsBilling) {
      const billingValues = watch();
      setValue('shipping_firstname', billingValues.first_name);
      setValue('shipping_lastname', billingValues.last_name);
      setValue('shipping_company', billingValues.company);
      setValue('shipping_address_1', billingValues.address);
      setValue('shipping_address_2', billingValues.address_cont);
      setValue('shipping_city', billingValues.city);
      setValue('shipping_state', billingValues.state);
      setValue('shipping_zip', billingValues.zip);
      setValue('shipping_country', billingValues.country || 'US');
    }
  }, [sameAsBilling, watch, setValue]);

  // Domain and provider management functions
  const loadProvidersForDomain = async (domain: string) => {
    if (providersByDomain[domain]) return; // Already loaded
    
    try {
      setLoadingProviders(prev => ({ ...prev, [domain]: true }));
      const providers = await SettingsAPI.getProvidersByDomain(domain);
      setProvidersByDomain(prev => ({ ...prev, [domain]: providers }));
    } catch (error) {
      console.error(`Failed to load providers for ${domain}:`, error);
      setProvidersByDomain(prev => ({ ...prev, [domain]: [] }));
    } finally {
      setLoadingProviders(prev => ({ ...prev, [domain]: false }));
    }
  };

  const handleDomainChange = (domain: string, checked: boolean) => {
    let newDomains: string[];
    if (checked) {
      newDomains = [...selectedDomains, domain];
      loadProvidersForDomain(domain); // Load providers for this domain
    } else {
      newDomains = selectedDomains.filter(d => d !== domain);
      // Remove provider selection for this domain
      const currentProviders = watch('utility_providers') || {};
      const newProviders = { ...currentProviders };
      delete newProviders[domain];
      setValue('utility_providers', newProviders);
    }
    
    setSelectedDomains(newDomains);
    setValue('sale_domains', newDomains);
  };

  const handleProviderChange = (domain: string, providerId: string) => {
    const currentProviders = watch('utility_providers') || {};
    const newProviders = { ...currentProviders };
    
    if (providerId) {
      newProviders[domain] = providerId;
    } else {
      delete newProviders[domain];
    }
    
    setValue('utility_providers', newProviders);
  };

  // Helper: check which routing categories are missing
  const getMissingCategories = (): string[] => {
    if (!myRouting) return ['visa', 'mastercard', 'other'];
    const missing: string[] = [];
    if (!myRouting.visa) missing.push('Visa');
    if (!myRouting.mastercard) missing.push('MasterCard');
    if (!myRouting.other) missing.push('Other');
    return missing;
  };

  const allRoutingConfigured = myRouting ? !!(myRouting.visa && myRouting.mastercard && myRouting.other) : false;

  // Auto-select processor based on card number, per-agent routing, and processor settings
  useEffect(() => {
    if (!settingsLoaded || !processorSettings || !routingLoaded) {
      return;
    }

    // If global test mode is enabled, override everything
    if (processorSettings.testModeEnabled) {
      setValue('processor_id', '');
      setValue('nmi_account', '');
      setValue('test_mode', true);
      setRoutingMissing(false);
      return;
    }

    // No card entered yet — don't set routingMissing, just clear processor
    if (!ccnumber || ccnumber.replace(/\D/g, '').length < 4) {
      setValue('processor_id', '');
      setValue('nmi_account', '');
      setValue('test_mode', false);
      setRoutingMissing(false);
      return;
    }

    // Card entered — check per-agent routing for this card's category
    const brand = detectCardBrand(ccnumber);
    const category = mapBrandToCategory(brand);
    const routedProcessorId = myRouting ? myRouting[category] : null;

    if (routedProcessorId) {
      const accountKey = (category + 'Account') as keyof typeof myRouting;
      const nmiAccount = myRouting ? myRouting[accountKey] || '' : '';
      setValue('processor_id', routedProcessorId);
      setValue('nmi_account', nmiAccount);
      setValue('test_mode', false);
      setRoutingMissing(false);
      return;
    }

    // Card entered but no routing for this category — block submission
    setValue('processor_id', '');
    setValue('nmi_account', '');
    setValue('test_mode', false);
    setRoutingMissing(true);
  }, [ccnumber, setValue, processorSettings, settingsLoaded, myRouting, routingLoaded]);

  const resetForm = () => {
    // Reset ALL form fields to initial values
    reset({
      // Order Information
      orderid: '',
      ponumber: '',
      order_description: '',
      shipping_amount: undefined,
      sales_tax: undefined,
      tax_exempt: false,
      date_of_birth: '',
      account_number: '',
      ssn: '',
      drivers_license_number: '',
      drivers_license_state: '',
      dl_issuing_date: '',
      dl_expiration_date: '',

      // Card Information
      ccnumber: '',
      ccexp: '',
      cvv: '',

      // Payment Amount
      amount: undefined,
      currency: 'USD',

      // Billing Information
      first_name: '',
      last_name: '',
      company: '',
      address: '',
      address_cont: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      phone_number: '',
      email_address: '',
      website_address: '',
      fax_number: '',

      // Shipping Information
      same_as_billing: false,
      shipping_firstname: '',
      shipping_lastname: '',
      shipping_company: '',
      shipping_address_1: '',
      shipping_address_2: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
      shipping_country: 'US',

      // Sale Information
      test_mode: false,
      sale_type: ALLOWED_CATEGORIES[0] as typeof ALLOWED_CATEGORIES[number],
      sale_state: US_STATES[0] as typeof US_STATES[number],
      processor_id: '',
      nmi_account: '',
      sale_domains: [],
      utility_providers: {},
      sale_notes: '',
      tfn: '',
    });

    // Reset domain selection state
    setSelectedDomains([]);

    // Clear provider data
    setProvidersByDomain({});
  };

  // Wrapper that fetches fresh settings + decides whether to show the
  // 3RI-disabled confirmation dialog before running `action`. Used for
  // direct sales / self-authorization (which run 3RI live off this toggle)
  // and link-creation; for the latter the link's snapshot is taken
  // server-side, so confirming here surfaces the implication to the agent.
  const guardedSubmit = async (action: () => void) => {
    setCheckingThreeDs(true);
    let threeDsOn = true;
    try {
      const fresh = await SettingsAPI.getProcessorSettings();
      threeDsOn = fresh.threeDsEnabled !== false;
      setProcessorSettings(fresh);
    } catch (err) {
      console.warn('[PaymentForm] Could not refresh 3RI setting; proceeding with cached value', err);
      threeDsOn = processorSettings?.threeDsEnabled !== false;
    } finally {
      setCheckingThreeDs(false);
    }

    // QuickClick links: the ERP's 3DS doesn't apply, so there's nothing to confirm.
    if (qcLink) {
      action();
      return;
    }

    // Admin payment links: the per-link toggle is the sole authority, so confirm
    // based on the admin's choice — not the global toggle. Toggle off => warn
    // before sending. (Other elevated roles go through approval, so their toggle
    // is only a proposal and is not gated here — the approver confirms it.)
    if (isAdmin && paymentMode === 'payment_link') {
      if (linkThreeDs) {
        action();
        return;
      }
      setPendingAction(() => action);
      setThreeDsConfirmOpen(true);
      return;
    }

    // Toggle on — submit silently, no dialog.
    if (threeDsOn) {
      action();
      return;
    }

    // Toggle off — confirm with the user before submitting.
    setPendingAction(() => action);
    setThreeDsConfirmOpen(true);
  };

  const processForm = (type: 'sale' | 'authorize' | 'validate' | 'sale-3ri') => {
    handleSubmit(
      // Success callback - called when validation passes
      async (data) => {
        // Create a copy of the data
        const finalData = { ...data };

        console.log('[PaymentForm] RAW form data before processing:', {
          processor_id: data.processor_id,
          test_mode: data.test_mode,
          testModeEnabled: processorSettings?.testModeEnabled,
          ccnumber: data.ccnumber ? `${data.ccnumber.slice(0,4)}****` : 'none'
        });

        // If processor_id is empty (test mode), remove it from the payload entirely
        if (!data.processor_id || data.processor_id === '') {
          delete finalData.processor_id;
          console.log('[PaymentForm] Empty processor_id - removed from payload (test mode)');
        } else {
          console.log('[PaymentForm] Submitting with processor_id:', data.processor_id);
        }

        // If nmi_account is empty, remove it from the payload
        if (!data.nmi_account || data.nmi_account === '') {
          delete finalData.nmi_account;
        } else {
          console.log('[PaymentForm] Submitting with nmi_account:', data.nmi_account);
        }

        console.log('[PaymentForm] Test mode flag:', finalData.test_mode);
        console.log('[PaymentForm] Final payload keys:', Object.keys(finalData));
        console.log('[PaymentForm] Final payload processor_id:', finalData.processor_id);

        try {
          await onSubmit(finalData as PaymentFormData, type);
          // Success - show dialog and reset form
          setDialogType('success');
          setDialogMessage('TRANSACTION SUCCESSFUL');
          setDialogOpen(true);
          resetForm();
        } catch (error: any) {
          // Error - show error dialog
          setDialogType('error');
          setDialogMessage(error?.message || 'Transaction failed. Please try again.');
          setDialogOpen(true);
        }
      },
      // Error callback - called when validation fails
      (errors) => {
        console.log('Validation errors:', errors);
        // Scroll to the first error field
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (element as HTMLElement).focus();
          }
        }
      }
    )();
  };

  // deliveryMethod is passed only for the admin direct-send path (Email/SMS/Both
  // buttons → sends immediately). When omitted, the link is submitted for
  // supervisor approval; preferredOverride records the agent's requested channel
  // (email/sms/both) for the approver to see and default to.
  const submitForApproval = async (
    deliveryMethod?: "email" | "sms" | "both",
    preferredOverride?: "email" | "sms" | "both",
  ) => {
    const data = getValues();

    // Validate with paymentLinkSchema (no card field requirements)
    const result = paymentLinkSchema.safeParse(data);
    if (!result.success) {
      const fieldErrors = result.error.issues;
      console.log('Payment link validation errors:', fieldErrors);
      // Scroll to first error field
      if (fieldErrors.length > 0) {
        const firstField = fieldErrors[0].path[0] as string;
        const element = document.querySelector(`[name="${firstField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (element as HTMLElement).focus();
        }
      }
      // Show first error in dialog
      setDialogType('error');
      setDialogMessage(fieldErrors.map(e => e.message).join(', '));
      setDialogOpen(true);
      return;
    }

    // Admin direct-send: every chosen channel must have a destination
    // ("both" requires email AND phone).
    const sendNeedsSms = deliveryMethod === "sms" || deliveryMethod === "both";
    const sendNeedsEmail = deliveryMethod === "email" || deliveryMethod === "both";
    if (sendNeedsSms && !result.data.phone_number) {
      setDialogType('error');
      setDialogMessage('Phone number is required to send via SMS.');
      setDialogOpen(true);
      return;
    }
    if (sendNeedsEmail && !result.data.email_address) {
      setDialogType('error');
      setDialogMessage('Email address is required to send via Email.');
      setDialogOpen(true);
      return;
    }
    // Approval path: a supervisor chooses email vs SMS later, so just make sure
    // at least one contact channel exists for the link to be sent on.
    if (!deliveryMethod && !result.data.email_address && !result.data.phone_number) {
      setDialogType('error');
      setDialogMessage('Provide an email address or phone number so the link can be delivered after approval.');
      setDialogOpen(true);
      return;
    }

    setSendingLink(true);
    try {
      const validated = result.data;
      const linkData = {
        amount: validated.amount,
        currency: validated.currency,
        first_name: validated.first_name,
        last_name: validated.last_name,
        company: validated.company,
        address: validated.address,
        address_cont: validated.address_cont,
        city: validated.city,
        state: validated.state,
        zip: validated.zip,
        country: validated.country,
        phone_number: validated.phone_number,
        email_address: validated.email_address,
        website_address: validated.website_address,
        fax_number: validated.fax_number,
        orderid: validated.orderid,
        ponumber: validated.ponumber,
        order_description: validated.order_description,
        shipping_amount: validated.shipping_amount,
        sales_tax: validated.sales_tax,
        tax_exempt: validated.tax_exempt,
        same_as_billing: validated.same_as_billing,
        shipping_firstname: validated.shipping_firstname,
        shipping_lastname: validated.shipping_lastname,
        shipping_company: validated.shipping_company,
        shipping_address_1: validated.shipping_address_1,
        shipping_address_2: validated.shipping_address_2,
        shipping_city: validated.shipping_city,
        shipping_state: validated.shipping_state,
        shipping_zip: validated.shipping_zip,
        shipping_country: validated.shipping_country,
        date_of_birth: validated.date_of_birth,
        account_number: validated.account_number,
        ssn: validated.ssn,
        drivers_license_number: validated.drivers_license_number,
        drivers_license_state: validated.drivers_license_state,
        dl_issuing_date: validated.dl_issuing_date,
        dl_expiration_date: validated.dl_expiration_date,
        test_mode: validated.test_mode,
        sale_type: validated.sale_type,
        sale_state: validated.sale_state,
        processor_id: validated.processor_id || undefined,
        nmi_account: validated.nmi_account || undefined,
        sale_domains: validated.sale_domains,
        utility_providers: validated.utility_providers,
        sale_notes: validated.sale_notes,
        tfn: validated.tfn,
        // Admin direct-send uses the clicked channel; everyone else records the
        // agent's preferred channel so the approver can see it (and default to it).
        delivery_method: deliveryMethod || preferredOverride || 'email',
        // Elevated roles set the per-link 3DS requirement here. For admins it's
        // final; for director/floor_manager/team_lead it's the proposed value
        // their approver defaults to. Omitted for plain agents.
        ...(canToggle3ds ? { three_ds_required: linkThreeDs } : {}),
      };

      await PaymentLinkAPI.create(linkData);
      setDialogType('success');
      if (deliveryMethod) {
        const sentVia = deliveryMethod === 'both' ? 'Email & SMS' : deliveryMethod === 'sms' ? 'SMS' : 'Email';
        setDialogMessage(`Payment link created and sent via ${sentVia}!`);
      } else {
        setDialogMessage('Payment request submitted for approval. A supervisor will review it, set 3DS, and send the link to the customer.');
      }
      setDialogOpen(true);
      resetForm();
    } catch (error: any) {
      setDialogType('error');
      setDialogMessage(error?.response?.data?.detail || error?.message || 'Failed to submit payment request.');
      setDialogOpen(true);
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <div className="payment-form">
      <form>
        {/* Payment Mode Toggle */}
        <div className="payment-mode-toggle">
          {/* Direct Payment is hidden from the UI (logic kept intact). All new
              payments go through Send Payment Link or Self Authorization. */}
          {false && (
            <button
              type="button"
              className={`mode-btn ${paymentMode === 'direct' ? 'active' : ''}`}
              onClick={() => setPaymentMode('direct')}
            >
              Direct Payment
            </button>
          )}
          <button
            type="button"
            className={`mode-btn ${paymentMode === 'payment_link' ? 'active' : ''}`}
            onClick={() => setPaymentMode('payment_link')}
          >
            Send Payment Link
          </button>
          {/* Self Authorization is restricted to floor managers and above. */}
          {canSelfAuth && (
            <button
              type="button"
              className={`mode-btn ${paymentMode === 'self_authorization' ? 'active' : ''}`}
              onClick={() => setPaymentMode('self_authorization')}
            >
              3RI
            </button>
          )}
        </div>

        <div className="form-container">
          {/* First Column */}
          <div className="form-column">
            <div className="form-section">
              <h3>Sale Information</h3>
              <div className="form-group">
                <label>Sale Type*</label>
                <select {...register('sale_type')} required className={`required-field ${errors.sale_type ? 'error-field' : ''}`}>
                  {ALLOWED_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {errors.sale_type && <span className="error">{String(errors.sale_type?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Sale State*</label>
                <select {...register('sale_state')} required className={`required-field ${errors.sale_state ? 'error-field' : ''}`}>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.sale_state && <span className="error">{String(errors.sale_state?.message)}</span>}
              </div>
              <div className="form-group">
                <label className="checkbox-container" htmlFor="test-mode">
                  <input 
                    type="checkbox" 
                    id="test-mode"
                    {...register('test_mode')} 
                    disabled  // Auto-controlled by processor settings
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">
                    Test Mode {watch('test_mode') ? '(Active)' : '(Inactive)'}
                  </span>
                </label>
                <small className="field-info">
                  Automatically set based on processor settings
                </small>
              </div>

              {/* New Sale Domain Selection */}
              <div className="form-group">
                <label className="required-label">Sale Domains *</label>
                <div className={`checkbox-group-inline ${errors.sale_domains ? 'error-field' : ''}`}>
                  {['Electricity', 'Gas', 'Water'].map(domain => (
                    <label key={domain} className="checkbox-container" htmlFor={`domain-${domain}`}>
                      <input
                        type="checkbox"
                        id={`domain-${domain}`}
                        checked={selectedDomains.includes(domain)}
                        onChange={(e) => handleDomainChange(domain, e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      <span className="checkbox-label">
                        {domain}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.sale_domains && <span className="error">{String(errors.sale_domains?.message)}</span>}
                <small className="field-info">
                  Select at least one utility domain for this sale
                </small>
              </div>

              {/* Dynamic Provider Selection */}
              {selectedDomains.map(domain => (
                <div key={domain} className="form-group">
                  <label className="required-label">{domain} Provider *</label>
                  <SearchableProviderSelect
                    options={providersByDomain[domain] || []}
                    value={watch('utility_providers')?.[domain] || ''}
                    onChange={(id) => handleProviderChange(domain, id)}
                    disabled={loadingProviders[domain]}
                    placeholder={loadingProviders[domain] ? 'Loading providers...' : 'Select a provider...'}
                    hasError={!!errors.utility_providers}
                  />
                  {loadingProviders[domain] && (
                    <small className="field-info">Loading providers...</small>
                  )}
                  {providersByDomain[domain] && providersByDomain[domain].length === 0 && !loadingProviders[domain] && (
                    <small className="field-info text-warning">No providers available for {domain}</small>
                  )}
                </div>
              ))}
              {errors.utility_providers && <span className="error">{String(errors.utility_providers?.message)}</span>}

              {/* Sale Notes */}
              <div className="form-group">
                <label className="required-label">Sale Notes *</label>
                <textarea
                  {...register('sale_notes')}
                  placeholder="Internal notes about this sale..."
                  rows={3}
                  className={`form-textarea required-field ${errors.sale_notes ? 'error-field' : ''}`}
                />
                {errors.sale_notes && <span className="error">{String(errors.sale_notes?.message)}</span>}
                <small className="field-info">
                  Internal notes for team reference (required)
                </small>
              </div>
            </div>

            <div className="form-section">
              <h3>Payment Amount</h3>
              <div className="form-group">
                <label>Amount*</label>
                <input 
                  type="number" 
                  step="0.01"
                  {...register('amount')}
                  className={`required-field ${errors.amount ? 'error-field' : ''}`}
                />
                {errors.amount && <span className="error">{String(errors.amount?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Currency</label>
                <input
                  type="text"
                  {...register('currency')}
                  value="USD"
                  readOnly
                />
              </div>
              {/* TFN dropdown — mandatory. Options are the active Toll-Free
                  Numbers managed in Settings. Shows the label, submits the
                  number string. */}
              <div className="form-group">
                <label>TFN*</label>
                <select
                  {...register('tfn')}
                  className={`required-field ${errors.tfn ? 'error-field' : ''}`}
                >
                  <option value="">Select a TFN…</option>
                  {tfns.map((t) => (
                    <option key={t.id} value={t.number}>
                      {t.label ? `${t.label} (${t.number})` : t.number}
                    </option>
                  ))}
                </select>
                {tfns.length === 0 && (
                  <span className="error">No TFNs available. A user with Settings access must add one first.</span>
                )}
                {errors.tfn && <span className="error">{String(errors.tfn?.message)}</span>}
              </div>
              {/* Per-link 3DS toggle — admin, director, floor_manager and
                  sales_team_lead (payment-link mode only). For an admin it's the
                  final, authoritative value; for the others it's the proposed
                  value their approver confirms at approval time. */}
              {canToggle3ds && paymentMode === 'payment_link' && !(processorSettings as any)?.useQuickclick && (
                <div className="form-group">
                  <div className="admin-3ds-toggle">
                    <label className="admin-3ds-toggle-label">
                      <input
                        type="checkbox"
                        checked={linkThreeDs}
                        onChange={(e) => setLinkThreeDs(e.target.checked)}
                      />
                      <span>Require 3DS authentication</span>
                    </label>
                    <span className="admin-3ds-toggle-hint">
                      {isAdmin
                        ? (linkThreeDs
                            ? 'Customer must complete 3DS — enforced for every cardholder.'
                            : '3DS will be skipped for this link (no liability shift).')
                        : (linkThreeDs
                            ? 'Proposed: require 3DS. Your approver confirms before the link is sent.'
                            : 'Proposed: skip 3DS. Your approver confirms before the link is sent.')}
                    </span>
                  </div>
                </div>
              )}
              {canToggle3ds && paymentMode === 'payment_link' && (processorSettings as any)?.useQuickclick && (
                <div className="form-group">
                  <span className="admin-3ds-toggle-hint">
                    QuickClick is on — 3DS is handled account-wide by NMI, so the per-link 3DS toggle doesn't apply to these payments.
                  </span>
                </div>
              )}
            </div>

            {(paymentMode === 'direct' || paymentMode === 'self_authorization') && (
            <div className="form-section">
              <h3>Card Information</h3>
              <div className="form-group">
                <label>Card Number*</label>
                <input
                  type="text"
                  {...register('ccnumber')}
                  maxLength={23}
                  placeholder="8699452793177918"
                  className={`required-field ${errors.ccnumber ? 'error-field' : ''}`}
                  // Strip spaces, hyphens, slashes and any non-digit so a card
                  // entered as "8699 4527 9317 7918" or "8699-4527-..." submits
                  // as clean digits. Cap at 19 (max PAN length).
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 19);
                    setValue('ccnumber', cleaned, { shouldValidate: true });
                  }}
                />
                {errors.ccnumber && <span className="error">{String(errors.ccnumber?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Expiry (MMYY)*</label>
                <input
                  type="text"
                  {...register('ccexp')}
                  maxLength={7}
                  placeholder="1832"
                  className={`required-field ${errors.ccexp ? 'error-field' : ''}`}
                  // Strip spaces, slashes, hyphens and any other non-digit so
                  // "12/25", "12-25" or "12 25" all normalize to "1225" — NMI
                  // expects MMYY, so this must never trip the 4-digit validator.
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setValue('ccexp', cleaned, { shouldValidate: true });
                  }}
                />
                {errors.ccexp && <span className="error">{String(errors.ccexp?.message)}</span>}
              </div>
              <div className="form-group">
                <label>CVV*</label>
                <input
                  type="text"
                  {...register('cvv')}
                  maxLength={4}
                  placeholder="395"
                  className={`required-field ${errors.cvv ? 'error-field' : ''}`}
                />
                {errors.cvv && <span className="error">{String(errors.cvv?.message)}</span>}
              </div>
            </div>
            )}
          </div>

          {/* Second Column - Order Information */}
          <div className="form-column">
            <div className="form-section">
              <h3>Order Information</h3>
              <div className="form-group">
                <label>Processor (Auto-selected)</label>
                <input 
                  type="hidden" 
                  {...register('processor_id')} 
                />
                <input type="text" disabled value={(() => {
                    const currentProcessorId = watch('processor_id');
                    const missingCats = getMissingCategories();
                    const hasCard = ccnumber && ccnumber.replace(/\D/g, '').length >= 4;

                    // Test mode
                    if (processorSettings?.testModeEnabled) return 'Test Mode (No Processor)';

                    if (qcLink) return myRouting?.quickclickName || 'No Processor';

                    // No card entered yet
                    if (!hasCard) {
                      if (missingCats.length === 0) return 'Enter card number to auto-select processor';
                      if (missingCats.length === 3) return 'No Processor Configured';
                      return `Missing: ${missingCats.join(', ')}`;
                    }

                    // Card entered, routing matched
                    if (currentProcessorId && myRouting) {
                      const brand = detectCardBrand(ccnumber);
                      const category = mapBrandToCategory(brand);
                      const nameKey = (category + 'Name') as keyof typeof myRouting;
                      const routingName = myRouting[nameKey];
                      if (routingName) return String(routingName);
                      return currentProcessorId;
                    }

                    // Card entered, no routing for this category
                    if (routingMissing) {
                      const brand = detectCardBrand(ccnumber);
                      const category = mapBrandToCategory(brand);
                      return `No processor for ${category.charAt(0).toUpperCase() + category.slice(1)}`;
                    }

                    return currentProcessorId || 'No Processor';
                  })()} className="form-input" style={(() => {
                    const pid = watch('processor_id');
                    const missing = getMissingCategories();
                    const isTest = processorSettings?.testModeEnabled;
                    if (qcLink) return { borderColor: 'var(--success, #28a745)', color: 'var(--success, #28a745)' };
                    if (routingMissing || (!isTest && missing.length === 3)) return { borderColor: 'var(--error, #dc3545)', color: 'var(--error, #dc3545)' };
                    if (pid || (!isTest && missing.length === 0)) return { borderColor: 'var(--success, #28a745)', color: 'var(--success, #28a745)' };
                    if (!isTest && missing.length > 0) return { borderColor: 'var(--warning, #ffc107)', color: 'var(--warning, #b58900)' };
                    return undefined;
                  })()} />
                <small className="field-info" style={(() => {
                    const pid = watch('processor_id');
                    const missing = getMissingCategories();
                    const isTest = processorSettings?.testModeEnabled;
                    if (qcLink) return { color: 'var(--success, #28a745)' };
                    if (routingMissing) return { color: 'var(--error, #dc3545)' };
                    if (pid || (!isTest && missing.length === 0)) return { color: 'var(--success, #28a745)' };
                    if (!isTest && missing.length > 0) return { color: 'var(--warning, #b58900)' };
                    return undefined;
                  })()}>
                  {(() => {
                    const currentProcessorId = watch('processor_id');
                    const missingCats = getMissingCategories();
                    const hasCard = ccnumber && ccnumber.replace(/\D/g, '').length >= 4;

                    if (processorSettings?.testModeEnabled) return 'Test Mode — No processor assigned';

                    if (qcLink) return 'Assigned to you in Settings';

                    if (!hasCard) {
                      if (missingCats.length === 0) return 'All processors configured. Enter a card number to auto-select.';
                      if (missingCats.length === 3) return 'No processor routing configured. Contact an admin to set up routing in Settings.';
                      return `Missing routing for: ${missingCats.join(', ')}. Cards of those types cannot be charged.`;
                    }

                    if (currentProcessorId && myRouting) {
                      const brand = detectCardBrand(ccnumber);
                      const category = mapBrandToCategory(brand);
                      const nameKey = (category + 'Name') as keyof typeof myRouting;
                      const routingName = myRouting[nameKey];
                      if (routingName) return `Per-agent routing: ${routingName}`;
                    }

                    if (routingMissing) {
                      const brand = detectCardBrand(ccnumber);
                      const category = mapBrandToCategory(brand);
                      return `No processor configured for ${category.charAt(0).toUpperCase() + category.slice(1)} cards. Contact an admin.`;
                    }

                    return '';
                  })()}
                </small>
              </div>
              <div className="form-group">
                <label>Order ID</label>
                <input type="text" {...register('orderid')} />
              </div>
              <div className="form-group">
                <label>PO Number</label>
                <input type="text" {...register('ponumber')} />
              </div>
              <div className="form-group">
                <label>Order Description</label>
                <textarea {...register('order_description')} rows={3} />
              </div>
              <div className="form-group">
                <label>Shipping Amount</label>
                <input 
                  type="number" 
                  step="0.01" 
                  {...register('shipping_amount')} 
                />
              </div>
              <div className="form-group">
                <label>Sales Tax</label>
                <input 
                  type="number" 
                  step="0.01" 
                  {...register('sales_tax')} 
                />
              </div>
              <div className="form-group">
                <label className="checkbox-container" htmlFor="tax-exempt">
                  <input type="checkbox" id="tax-exempt" {...register('tax_exempt')} />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">
                    Tax Exempt
                  </span>
                </label>
              </div>
              <div className="form-group">
                <label>Date of Birth (MM/DD/YYYY)</label>
                <input 
                  type="text" 
                  {...register('date_of_birth')} 
                  placeholder="09/10/1936"
                  className={errors.date_of_birth ? 'error-field' : ''}
                />
                {errors.date_of_birth && <span className="error">{String(errors.date_of_birth?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input type="text" {...register('account_number')} />
              </div>
              <div className="form-group">
                <label>SSN*</label>
                <input
                  type="text"
                  inputMode="numeric"
                  {...register('ssn', {
                    onChange: (e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 9); },
                  })}
                  maxLength={9}
                  placeholder="769388460"
                  className={`required-field ${errors.ssn ? 'error-field' : ''}`}
                />
                {errors.ssn && <span className="error">{String(errors.ssn?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Driver's License Number</label>
                <input
                  type="text"
                  {...register('drivers_license_number')}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Issuing State</label>
                <select {...register('drivers_license_state')} defaultValue="">
                  <option value="">-- Optional --</option>
                  {US_STATE_CODES.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>DLN Issuing Date</label>
                <input type="text" {...register('dl_issuing_date')} placeholder="MM/DD/YYYY (optional)" className={errors.dl_issuing_date ? 'error-field' : ''} />
                {errors.dl_issuing_date && <span className="error">{String(errors.dl_issuing_date?.message)}</span>}
              </div>
              <div className="form-group">
                <label>DLN Expiration Date</label>
                <input type="text" {...register('dl_expiration_date')} placeholder="MM/DD/YYYY (optional)" className={errors.dl_expiration_date ? 'error-field' : ''} />
                {errors.dl_expiration_date && <span className="error">{String(errors.dl_expiration_date?.message)}</span>}
              </div>
            </div>
          </div>

          {/* Third Column - Billing Information */}
          <div className="form-column">
            <div className="form-section">
              <h3>Billing Information</h3>
              <div className="form-group">
                <label>First Name*</label>
                <input type="text" {...register('first_name')} className={`required-field ${errors.first_name ? 'error-field' : ''}`} />
                {errors.first_name && <span className="error">{String(errors.first_name?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Last Name*</label>
                <input type="text" {...register('last_name')} className={`required-field ${errors.last_name ? 'error-field' : ''}`} />
                {errors.last_name && <span className="error">{String(errors.last_name?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Company</label>
                <input type="text" {...register('company')} />
              </div>
              <div className="form-group">
                <label>Address*</label>
                <input type="text" {...register('address')} className={`required-field ${errors.address ? 'error-field' : ''}`} />
                {errors.address && <span className="error">{String(errors.address?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input type="text" {...register('address_cont')} />
              </div>
              <div className="form-group">
                <label>City*</label>
                <input type="text" {...register('city')} className={`required-field ${errors.city ? 'error-field' : ''}`} />
                {errors.city && <span className="error">{String(errors.city?.message)}</span>}
              </div>
              <div className="form-group">
                <label>State*</label>
                <select {...register('state')} className={`required-field ${errors.state ? 'error-field' : ''}`}>
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state && <span className="error">{String(errors.state?.message)}</span>}
              </div>
              <div className="form-group">
                <label>ZIP Code*</label>
                <input type="text" {...register('zip')} maxLength={5} className={`required-field ${errors.zip ? 'error-field' : ''}`} />
                {errors.zip && <span className="error">{String(errors.zip?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Country</label>
                <input 
                  type="text" 
                  {...register('country')} 
                  value="US"
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  {...register('phone_number')}
                  placeholder="1234567890"
                  maxLength={10}
                  className={errors.phone_number ? 'error-field' : ''}
                />
                {errors.phone_number && <span className="error">{String(errors.phone_number?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  {...register('email_address')}
                  placeholder="user@example.com"
                  className={errors.email_address ? 'error-field' : ''}
                />
                {errors.email_address && <span className="error">{String(errors.email_address?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Website Address</label>
                <input type="text" {...register('website_address')} />
              </div>
              <div className="form-group">
                <label>Fax Number</label>
                <input
                  type="text"
                  {...register('fax_number')}
                  placeholder="123-456-7890"
                  className={errors.fax_number ? 'error-field' : ''}
                />
                {errors.fax_number && <span className="error">{String(errors.fax_number?.message)}</span>}
              </div>
            </div>
          </div>

          {/* Fourth Column - Service Address (billing is auto-used as NMI shipping) */}
          <div className="form-column">
            <div className="form-section">
              <h3>Service Address</h3>
              <div className="form-group">
                <label className="checkbox-container" htmlFor="same-as-billing">
                  <input type="checkbox" id="same-as-billing" {...register('same_as_billing')} />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">
                    Same as Billing Address
                  </span>
                </label>
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input 
                  type="text" 
                  {...register('shipping_firstname')} 
                  disabled={sameAsBilling}
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input 
                  type="text" 
                  {...register('shipping_lastname')} 
                  disabled={sameAsBilling}
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <input 
                  type="text" 
                  {...register('shipping_company')} 
                  disabled={sameAsBilling}
                />
              </div>
              <div className="form-group">
                <label>Address Line 1{!sameAsBilling && '*'}</label>
                <input
                  type="text"
                  {...register('shipping_address_1')}
                  disabled={sameAsBilling}
                  className={errors.shipping_address_1 ? 'error-field' : ''}
                />
                {errors.shipping_address_1 && <span className="error">{String(errors.shipping_address_1?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input 
                  type="text" 
                  {...register('shipping_address_2')} 
                  disabled={sameAsBilling}
                />
              </div>
              <div className="form-group">
                <label>City{!sameAsBilling && '*'}</label>
                <input
                  type="text"
                  {...register('shipping_city')}
                  disabled={sameAsBilling}
                  className={errors.shipping_city ? 'error-field' : ''}
                />
                {errors.shipping_city && <span className="error">{String(errors.shipping_city?.message)}</span>}
              </div>
              <div className="form-group">
                <label>State{!sameAsBilling && '*'}</label>
                <select
                  {...register('shipping_state')}
                  disabled={sameAsBilling}
                  className={errors.shipping_state ? 'error-field' : ''}
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.shipping_state && <span className="error">{String(errors.shipping_state?.message)}</span>}
              </div>
              <div className="form-group">
                <label>ZIP Code{!sameAsBilling && '*'}</label>
                <input
                  type="text"
                  {...register('shipping_zip')}
                  disabled={sameAsBilling}
                  maxLength={5}
                  className={errors.shipping_zip ? 'error-field' : ''}
                />
                {errors.shipping_zip && <span className="error">{String(errors.shipping_zip?.message)}</span>}
              </div>
              <div className="form-group">
                <label>Country</label>
                <input 
                  type="text" 
                  {...register('shipping_country')} 
                  value="US"
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          {routingMissing && paymentMode !== 'payment_link' && (
            <p style={{ color: 'var(--error, #dc3545)', fontSize: '0.85rem', margin: '0 0 0.5rem 0', textAlign: 'center' }}>
              Payments disabled — no processor routing configured for this card type.
            </p>
          )}
          {paymentMode === 'payment_link' && !allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink && (
            <p style={{ color: 'var(--error, #dc3545)', fontSize: '0.85rem', margin: '0 0 0.5rem 0', textAlign: 'center' }}>
              Payment links disabled — all 3 processor routings (Visa, MasterCard, Other) must be configured. Contact an admin.
            </p>
          )}
          {paymentMode === 'direct' ? (
            <button
              type="button"
              onClick={() => guardedSubmit(() => processForm('sale'))}
              disabled={loading || routingMissing || checkingThreeDs}
              className="btn-primary"
            >
              {loading ? 'Processing...' : checkingThreeDs ? 'Checking…' : 'SALE'}
            </button>
          ) : paymentMode === 'self_authorization' ? (
            <button
              type="button"
              onClick={() => guardedSubmit(() => processForm('sale-3ri'))}
              disabled={loading || routingMissing || checkingThreeDs}
              className="btn-primary"
            >
              {loading ? 'Authenticating...' : checkingThreeDs ? 'Checking…' : '3RI & SALE'}
            </button>
          ) : (
            <div className="payment-link-buttons">
              {isAdmin ? (
                // Admin: top of hierarchy, no approval needed. Send immediately
                // via the chosen channel (3DS chosen above is authoritative).
                // Only the channels with a destination entered are offered.
                <>
                  {hasEmail && (
                    <button
                      type="button"
                      onClick={() => guardedSubmit(() => submitForApproval("email"))}
                      disabled={sendingLink || checkingThreeDs || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link"
                    >
                      {sendingLink ? 'Sending...' : checkingThreeDs ? 'Checking…' : 'SEND EMAIL'}
                    </button>
                  )}
                  {hasPhone && (
                    <button
                      type="button"
                      onClick={() => guardedSubmit(() => submitForApproval("sms"))}
                      disabled={sendingLink || checkingThreeDs || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link btn-sms"
                    >
                      {sendingLink ? 'Sending...' : checkingThreeDs ? 'Checking…' : 'SEND MESSAGE'}
                    </button>
                  )}
                  {hasEmail && hasPhone && (
                    <button
                      type="button"
                      onClick={() => guardedSubmit(() => submitForApproval("both"))}
                      disabled={sendingLink || checkingThreeDs || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link btn-both"
                    >
                      {sendingLink ? 'Sending...' : checkingThreeDs ? 'Checking…' : 'SEND BOTH'}
                    </button>
                  )}
                  {!hasEmail && !hasPhone && (
                    <span className="preferred-delivery-label">Enter an email address or phone number above to send the link.</span>
                  )}
                </>
              ) : (
                // Everyone below admin: the link goes to a supervisor for
                // approval. The agent picks the requested send channel here
                // (Email / SMS / Both) — the approver sees it as the default and
                // confirms (or changes) it before the link is actually sent.
                <>
                  <span className="preferred-delivery-label">Submit for approval — requested send method:</span>
                  {hasEmail && (
                    <button
                      type="button"
                      onClick={() => submitForApproval(undefined, 'email')}
                      disabled={sendingLink || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link"
                    >
                      {sendingLink ? 'Submitting...' : '✉️ EMAIL'}
                    </button>
                  )}
                  {hasPhone && (
                    <button
                      type="button"
                      onClick={() => submitForApproval(undefined, 'sms')}
                      disabled={sendingLink || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link btn-sms"
                    >
                      {sendingLink ? 'Submitting...' : '💬 SMS'}
                    </button>
                  )}
                  {hasEmail && hasPhone && (
                    <button
                      type="button"
                      onClick={() => submitForApproval(undefined, 'both')}
                      disabled={sendingLink || (!allRoutingConfigured && !processorSettings?.testModeEnabled && !qcLink)}
                      className="btn-primary btn-payment-link btn-both"
                    >
                      {sendingLink ? 'Submitting...' : '✉️💬 BOTH'}
                    </button>
                  )}
                  {!hasEmail && !hasPhone && (
                    <span className="preferred-delivery-label">Enter an email address or phone number above to submit for approval.</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </form>

      {/* 3DS-disabled confirmation dialog */}
      {threeDsConfirmOpen && (
        <div className="dialog-overlay" onClick={() => {
          setThreeDsConfirmOpen(false);
          setPendingAction(null);
        }}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon" style={{ color: 'var(--warning, #d97706)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="dialog-title">3DS Disabled</h3>
            <p className="dialog-message">
              3DS is disabled for this transaction. If you want to continue, accept it; otherwise deny.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
              <button
                className="dialog-button"
                style={{ flex: 1, background: 'var(--text-secondary, #6b7280)' }}
                onClick={() => {
                  setThreeDsConfirmOpen(false);
                  setPendingAction(null);
                }}
              >
                Deny
              </button>
              <button
                className="dialog-button"
                style={{ flex: 1 }}
                onClick={() => {
                  const action = pendingAction;
                  setThreeDsConfirmOpen(false);
                  setPendingAction(null);
                  if (action) action();
                }}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Result Dialog */}
      {dialogOpen && (
        <div className="dialog-overlay" onClick={() => {
          setDialogOpen(false);
          if (dialogType === 'success') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}>
          <div className={`dialog-box ${dialogType}`} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              {dialogType === 'success' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
            </div>
            <h3 className="dialog-title">{dialogType === 'success' ? 'Success' : 'Error'}</h3>
            <p className="dialog-message">{dialogMessage}</p>
            <button
              className="dialog-button"
              onClick={() => {
                setDialogOpen(false);
                if (dialogType === 'success') {
                  // Scroll to top of form after successful transaction
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <style>{`
        .payment-form {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .payment-mode-toggle {
          display: flex;
          gap: 0;
          margin-bottom: 1.5rem;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid var(--border-primary);
        }

        .mode-btn {
          flex: 1;
          padding: 0.75rem 1.5rem;
          border: none;
          background: var(--bg-card);
          color: var(--text-secondary);
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mode-btn:hover {
          background: var(--bg-hover, #f0f0f0);
        }

        .mode-btn.active {
          background: var(--accent-primary, #3b82f6);
          color: white;
        }

        .payment-link-buttons {
          display: flex;
          gap: 0.75rem;
          width: 100%;
          flex-wrap: wrap;
        }

        .admin-3ds-toggle {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.6rem 0.85rem;
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          background: var(--bg-secondary, #f9fafb);
          margin-bottom: 0.25rem;
        }

        .admin-3ds-toggle-label {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-primary);
          cursor: pointer;
        }

        .admin-3ds-toggle-label input {
          width: 1rem;
          height: 1rem;
          accent-color: #10b981;
          cursor: pointer;
        }

        .admin-3ds-toggle-hint {
          font-size: 0.78rem;
          color: var(--text-secondary);
          padding-left: 1.5rem;
        }

        .preferred-delivery {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .preferred-delivery-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .preferred-delivery-options {
          display: flex;
          gap: 0.5rem;
        }

        .preferred-delivery-options label {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .preferred-delivery-options label.active {
          border-color: #10b981;
          color: #059669;
          background: rgba(16, 185, 129, 0.1);
        }

        .preferred-delivery-options input {
          accent-color: #10b981;
        }

        .payment-link-buttons .btn-payment-link {
          flex: 1;
        }

        .btn-payment-link {
          background: #10b981 !important;
        }

        .btn-payment-link:hover {
          background: #059669 !important;
        }

        .btn-payment-link.btn-sms {
          background: #6366f1 !important;
        }

        .btn-payment-link.btn-sms:hover {
          background: #4f46e5 !important;
        }

        .btn-payment-link.btn-both {
          background: #8b5cf6 !important;
        }

        .btn-payment-link.btn-both:hover {
          background: #7c3aed !important;
        }

        .form-container {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }

        .form-column {
          display: flex;
          flex-direction: column;
        }

        .form-section {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .form-section h3 {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.375rem;
          font-weight: 500;
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 0.625rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 60px;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
        }

        .form-group input[readonly],
        .form-group input:disabled,
        .form-group select:disabled {
          background: var(--bg-secondary);
          color: var(--text-secondary);
          cursor: not-allowed;
        }


        .error {
          color: var(--danger);
          font-size: 0.75rem;
          margin-top: 0.25rem;
          display: block;
        }

        .error-field {
          border-color: var(--input-error) !important;
          box-shadow: 0 0 0 1px var(--input-error) !important;
        }

        /* Required field styling */
        .required-field {
          box-shadow: 0 0 0 1px var(--border-color);
          transition: box-shadow 0.2s ease;
        }

        .required-field:focus {
          box-shadow: 0 0 0 2px var(--primary-color);
          outline: none;
        }

        /* Override for error state on required fields */
        .required-field.error-field {
          box-shadow: 0 0 0 1px var(--input-error) !important;
        }

        .required-field.error-field:focus {
          box-shadow: 0 0 0 2px var(--input-error) !important;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border-primary);
        }

        .form-actions button {
          padding: 0.875rem 2rem;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .form-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 1400px) {
          .form-container {
            grid-template-columns: repeat(2, 1fr);
          }
        }


        .form-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          background: var(--input-bg);
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-select:focus {
          outline: none;
          border-color: var(--input-focus);
          box-shadow: var(--shadow-focus);
        }

        .form-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: var(--bg-secondary);
        }

        .form-textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          background: var(--input-bg);
          color: var(--text-primary);
          font-size: 0.9375rem;
          font-family: inherit;
          resize: vertical;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-textarea:focus {
          outline: none;
          border-color: var(--input-focus);
          box-shadow: var(--shadow-focus);
        }

        .text-warning {
          color: var(--warning);
        }

        @media (max-width: 992px) {
          .form-container {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .payment-form {
            padding: 1rem;
          }

          .form-container {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions button {
            width: 100%;
          }
        }

        /* Dialog Styles */
        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .dialog-box {
          background: var(--bg-card);
          border-radius: 12px;
          padding: 2rem;
          min-width: 320px;
          max-width: 400px;
          text-align: center;
          box-shadow: 0 10px 40px var(--shadow-heavy);
          border: 1px solid var(--border-primary);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .dialog-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dialog-box.success .dialog-icon {
          background: var(--success-light);
          color: var(--success);
        }

        .dialog-box.error .dialog-icon {
          background: var(--danger-light);
          color: var(--danger);
        }

        .dialog-icon svg {
          width: 32px;
          height: 32px;
        }

        .dialog-title {
          margin: 0 0 0.5rem;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dialog-message {
          margin: 0 0 1.5rem;
          color: var(--text-secondary);
          font-size: 1rem;
        }

        .dialog-button {
          background: var(--primary);
          color: var(--text-inverse);
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dialog-button:hover {
          background: var(--primary-hover);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px var(--shadow-medium);
        }

        .dialog-box.success .dialog-button {
          background: var(--success);
        }

        .dialog-box.success .dialog-button:hover {
          filter: brightness(1.1);
          box-shadow: 0 4px 8px var(--success-light);
        }

        .dialog-box.error .dialog-button {
          background: var(--danger);
        }

        .dialog-box.error .dialog-button:hover {
          background: var(--danger-hover);
          box-shadow: 0 4px 8px var(--danger-light);
        }
      `}</style>
    </div>
  );
};